import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Container
} from "@mui/material";
import {
  Search,
  LayoutGrid,
  List,
  Plus,
  Eye,
  Trash2,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  User
} from 'lucide-react';
import { tokens } from "../../theme";
import Modal from "../../utils/components/Modal";
import ReceiptForm from "../receipts/ReceiptForm";
import InvoiceForm from "./InvoiceForm";
import Cancellation from "../../utils/components/Cancellation";
import MultiStepsInvoiceForm from "./MultiStepsInvoiceForm";
import { getAllInvoices, deleteInvoice, deleteInvoices } from "./InvoiceService";
import { getAllUsers } from "../users/UserService";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import Constants from "../../utils/constants/Constants";
import CustomDataTable from "../../utils/components/CustomDataTable";

const Invoices = () => {
  const [ids, setIds] = useState([]);
  const setIsLoading = useLoadingSpinner();
  const [state, setState] = useState(false);
  const [params, setParams] = useState({ pageSize: 12, page: 0 });
  const [invoice, setInvoice] = useState(null);
  const [invoices, setInvoices] = useState({ content: [], totalElements: 0 });
  const [usersData, setUsersData] = useState({ content: [] });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  // High-Volume Optimization States
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lunchEditionModal = (inv) => {
    setInvoice(inv);
    setIsEditModalOpen(true);
  };

  const lunchDeletionModal = (selectedIds) => {
    setIds(Array.isArray(selectedIds) ? selectedIds : [selectedIds]);
    setIsDeletionModalOpen(true);
  };

  const lunchCreateModal = async () => {
    setIsCreateModalOpen(true);
  };

  const deleteInvoiceHandler = async () => {
    if (!!ids && ids.length > 0) {
      setIsLoading(true);
      try {
        if (ids.length === 1) {
          await deleteInvoice(ids[0], setState, setIsLoading);
        } else {
          await deleteInvoices(ids, setState, setIsLoading);
        }
        setIsDeletionModalOpen(false);
      } catch {
        // Error feedback is handled in the service layer.
      }
    }
  };

  const lunchReceiptModal = (invoiceId) => {
    const inv = displayInvoices.find((i) => i.id === invoiceId);
    setInvoice(inv);
    setIsReceiptModalOpen(true);
  };

  useEffect(() => {
    setIsLoading(true);
    getAllInvoices(setInvoices, setIsLoading, params);
  }, [state, setIsLoading, params]);

  useEffect(() => {
    getAllUsers(setUsersData, () => {}, { pageSize: "All", page: 0 });
  }, []);

  const usersByUserId = useMemo(
    () =>
      (usersData?.content ?? []).reduce((accumulator, user) => {
        if (user?.userId) {
          accumulator[user.userId] = user;
        }
        return accumulator;
      }, {}),
    [usersData]
  );

  const getInvoiceUser = useCallback((inv) => {
    if (inv?.user) {
      return inv.user;
    }

    if (inv?.userId) {
      return usersByUserId[inv.userId] ?? null;
    }

    return null;
  }, [usersByUserId]);

  const displayInvoices = useMemo(
    () =>
      (invoices.content ?? []).map((inv) => ({
        ...inv,
        user: getInvoiceUser(inv),
      })),
    [invoices, getInvoiceUser]
  );

  const getStatusConfig = (status) => {
    switch (status) {
      case Constants.INVOICE_STATUS.PAID:
        return { color: theme.palette.mode === 'dark' ? colors.greenAccent[500] : colors.greenAccent[300], icon: <CheckCircle2 size={16} />, label: "Paid" };
      case Constants.INVOICE_STATUS.DUE:
        return { color: colors.redAccent[500], icon: <AlertCircle size={16} />, label: "Due" };
      case Constants.INVOICE_STATUS.PARTIALLY_PAID:
        return { color: theme.palette.mode === 'dark' ? colors.greenAccent[400] : colors.greenAccent[300], icon: <Clock size={16} />, label: "Partial" };
      default:
        return { color: colors.grey[400], icon: <Clock size={16} />, label: status };
    }
  };

  const getInvoiceClientName = (inv) => {
    const client = getInvoiceUser(inv);
    if (!client) {
      return "Unknown Client";
    }

    return `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim() || "Unknown Client";
  };

  const filteredInvoices = displayInvoices.filter((inv) => {
    const clientName = getInvoiceClientName(inv).toLowerCase();
    const invoiceNumber = inv.invoiceNumber?.toLowerCase() ?? "";
    const searchValue = searchQuery.toLowerCase();
    const matchesSearch =
      clientName.includes(searchValue) || invoiceNumber.includes(searchValue);
    const matchesStatus = statusFilter === 'ALL' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const columns = [
    { field: "invoiceNumber", headerName: "Invoice #", width: 150 },
    {
      field: "clientName",
      headerName: "Client",
      width: 200,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" gap="8px">
            <User size={16} color={colors.grey[400]} />
            <Typography color={colors.grey[100]}>
              {getInvoiceClientName(params.row)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      width: 150,
      renderCell: (params) => {
        const total = params.row.totalAmount ?? (params.row.amountDue + params.row.totalAmountPaid);
        return (
          <Typography fontWeight="bold" color={colors.blueAccent[300]}>
            ${total?.toLocaleString() || "0.00"}
          </Typography>
        );
      }
    },
    {
      field: "amountDue",
      headerName: "Amount Due",
      width: 150,
      renderCell: (params) => (
        <Typography fontWeight="bold" color={colors.grey[100]}>
          ${params.value?.toLocaleString() || "0.00"}
        </Typography>
      )
    },
    {
      field: "totalAmountPaid",
      headerName: "Amount Paid",
      width: 150,
      renderCell: (params) => (
        <Typography fontWeight="bold" color={colors.greenAccent[500]}>
          ${params.value?.toLocaleString() || "0.00"}
        </Typography>
      )
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const config = getStatusConfig(params.value);
        return (
          <Chip
            label={config.label}
            size="small"
            sx={{ bgcolor: `${config.color}1A`, color: config.color, fontWeight: '700' }}
          />
        );
      }
    },
    {
      field: "issuedDate",
      headerName: "Issued Date",
      width: 150,
      renderCell: (params) => (
        <Typography color={colors.grey[300]}>
          {formatDate(params.value)}
        </Typography>
      )
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 150,
      renderCell: (params) => (
        <Typography color={colors.redAccent[400]} fontWeight="500">
          {formatDate(params.value)}
        </Typography>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          {/* <IconButton size="small" onClick={() => lunchEditionModal(params.row)} sx={{ color: colors.grey[100] }}>
            <Eye size={18} />
          </IconButton> */}
          <Tooltip title="View Receipt">
            <IconButton size="small" onClick={() => lunchReceiptModal(params.row.id)} sx={{ color: colors.blueAccent[500] }}>
              <FileText size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => lunchDeletionModal(params.row.id)} sx={{ color: colors.redAccent[500] }}>
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Details">
            <Button
              variant="text"
              size="small"
              startIcon={<Eye size={16} />}
              onClick={() => lunchEditionModal(params.row)}
              sx={{ color: colors.grey[100], textTransform: 'none' }}
            >
              Details
            </Button>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: "40px", pb: "80px" }}>
      {/* Enhanced Header Section */}
      <Box
        mb="40px"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          padding: "32px",
          borderRadius: "24px",
          border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="24px">
          <Box>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}>
              Invoices Management
            </Typography>
            <Typography variant="h5" color={colors.greenAccent[500]} fontWeight="500">
              {filteredInvoices.length} active invoices found in system
            </Typography>
          </Box>

          <Box display="flex" gap="12px" alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                borderRadius: '12px',
                p: '4px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.grey[300],
                  '&.Mui-selected': {
                    bgcolor: colors.greenAccent[500],
                    color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                    '&:hover': { bgcolor: colors.greenAccent[400] }
                  }
                }
              }}
            >
              <ToggleButton value="card"><LayoutGrid size={18} /></ToggleButton>
              <ToggleButton value="list"><List size={18} /></ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={lunchCreateModal}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                fontWeight: "bold",
                padding: "10px 24px",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: `0 8px 20px ${colors.greenAccent[500]}33`,
                "&:hover": {
                  backgroundColor: colors.greenAccent[400],
                  transform: "translateY(-2px)"
                },
                transition: "all 0.2s"
              }}
            >
              Create Invoice
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap="16px" alignItems="center">
          <TextField
            placeholder="Search by client or invoice number..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "#FFFFFF",
                '& fieldset': { borderColor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200] }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color={colors.grey[300]} />
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" gap="8px">
            {['ALL', 'PAID', 'DUE', 'PARTIALLY_PAID'].map((status) => (
              <Chip
                key={status}
                label={status.replace('_', ' ')}
                onClick={() => setStatusFilter(status)}
                sx={{
                  borderRadius: '10px',
                  fontWeight: '600',
                  bgcolor: statusFilter === status ? colors.blueAccent[500] : 'transparent',
                  color: statusFilter === status ? '#FFFFFF' : colors.grey[300],
                  border: `1px solid ${statusFilter === status ? colors.blueAccent[500] : colors.grey[400]}`,
                  '&:hover': { bgcolor: statusFilter === status ? colors.blueAccent[400] : 'rgba(255,255,255,0.05)' }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Conditional Rendering based on View Mode */}
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredInvoices.map((inv) => {
            const statusCfg = getStatusConfig(inv.status);
            return (
              <Grid item xs={12} md={6} lg={4} key={inv.id}>
                <Card
                  sx={{
                    borderRadius: "24px",
                    backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                    border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                      borderColor: colors.greenAccent[500],
                    }
                  }}
                >
                  <CardContent sx={{ p: "24px" }}>
                    <Box display="flex" justifyContent="space-between" mb="20px" alignItems="center">
                      <Chip
                        icon={React.cloneElement(statusCfg.icon, { size: 14, color: statusCfg.color })}
                        label={statusCfg.label}
                        sx={{
                          bgcolor: `${statusCfg.color}1A`,
                          color: statusCfg.color,
                          fontWeight: "700",
                          borderRadius: "10px",
                          "& .MuiChip-label": { px: 1 }
                        }}
                      />
                      <Typography variant="caption" color={colors.grey[400]} fontWeight="600">
                        {inv.invoiceNumber}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap="12px" mb="16px">
                      <Box sx={{ bgcolor: `${colors.blueAccent[500]}1A`, p: "10px", borderRadius: "12px" }}>
                        <User size={20} color={colors.blueAccent[500]} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                          {getInvoiceClientName(inv)}
                        </Typography>
                        <Typography variant="body2" color={colors.grey[400]}>
                          Client
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" justifyContent="space-between" mb="24px" p="16px" sx={{ bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[100], borderRadius: "16px" }}>
                      <Box>
                        <Typography variant="caption" color={colors.grey[400]} display="block" mb="4px">
                          Amount Due
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color={colors.greenAccent[500]}>
                          ${inv.amountDue?.toLocaleString() || "0.00"}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color={colors.grey[400]} display="block" mb="4px">
                          Due Date
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color={formatDate(inv.dueDate) === "-" ? colors.grey[400] : colors.redAccent[400]}>
                          {formatDate(inv.dueDate)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" gap="10px">
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Eye size={18} />}
                        onClick={() => lunchEditionModal(inv)}
                        sx={{
                          bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[200],
                          color: colors.grey[100],
                          boxShadow: "none",
                          borderRadius: "12px",
                          textTransform: "none",
                          fontWeight: "bold",
                          "&:hover": { bgcolor: colors.primary[300] }
                        }}
                      >
                        Details
                      </Button>
                      <Tooltip title="View Receipt">
                        <IconButton
                          onClick={() => lunchReceiptModal(inv.id)}
                          sx={{
                            bgcolor: `${colors.greenAccent[500]}1A`,
                            color: colors.greenAccent[500],
                            borderRadius: "12px",
                            p: "10px",
                            "&:hover": { bgcolor: colors.greenAccent[500], color: colors.primary[500] }
                          }}
                        >
                          <FileText size={20} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => lunchDeletionModal(inv.id)}
                          sx={{
                            bgcolor: `${colors.redAccent[500]}1A`,
                            color: colors.redAccent[500],
                            borderRadius: "12px",
                            p: "10px",
                            "&:hover": { bgcolor: colors.redAccent[500], color: "#FFFFFF" }
                          }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <CustomDataTable
          addRecordButtonTitle={"Add Invoice"}
          headerColumn={columns}
          lunchCreateModal={lunchCreateModal}
          lunchDeletionModal={lunchDeletionModal}
          paginationModel={params}
          rows={{ ...invoices, content: filteredInvoices }}
          setPaginationModel={setParams}
          tableTitle={"Invoices List"}
        />
      )}

      {/* Modals */}
      <Modal isOpen={isEditModalOpen} onClose={setIsEditModalOpen}>
        <InvoiceForm
          invoiceToEdit={invoice}
          isUpdating={true}
          setState={setState}
          toggleModal={setIsEditModalOpen}
        />
      </Modal>
      <Modal isOpen={isCreateModalOpen} onClose={setIsCreateModalOpen}>
        <MultiStepsInvoiceForm
          setState={setState}
          toggleModal={setIsCreateModalOpen}
          usersData={usersData}
        />
      </Modal>
      <Modal isOpen={isDeletionModalOpen} onClose={setIsDeletionModalOpen}>
        <Cancellation
          actionHandler={deleteInvoiceHandler}
          actionText={"Delete"}
          cancelText={"Go Back"}
          setOpen={setIsDeletionModalOpen}
          title={`Delete Invoice${ids.length > 1 ? "s" : ""}`}
          warningMessage={`Are you sure you want to delete ${ids.length > 1 ? "these invoices" : "this invoice"}?`}
        />
      </Modal>
      <Modal isOpen={isReceiptModalOpen} onClose={setIsReceiptModalOpen}>
        <ReceiptForm invoice={invoice} setState={setState} />
      </Modal>
    </Container>
  );
};

export default Invoices;
